import { useState, useEffect } from 'react'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import MosSmartDialog from '../common/MosSmartDialog'
import createDialogTitle from '../../../generic/utilities/dialogUtil'
import { apiGet, apiPost } from '../../../generic/Api_Functions'
import AlertUI from '../../../generic/AlertUI'
import { Typography, TextField, FormControl, InputLabel, Select, MenuItem, Stack, Box, CircularProgress, Button } from '@mui/material'
import useMosInfo from '../common/provider/useMosInfo'
import { getJobAutoReportResources, hasPendingRawMaterialResources, resolveReportOutcome } from '../common/reportOutcome'

const OK_QUALITY_TYPE_CODE = 'Ok'

const ReportQuantityDialog = ({ open, onCancel, onSuccess, jobReference, fabricationOrder, statusCode = 'ACTIVE_EXECUTION', quantity, hideOkOption = false, materials = [] }) => {
  const { t_ } = useSmartTranslation()
  const { device, worker, node, activeJobs, machineStorage } = useMosInfo()
  const [AlertElement, showAlert] = AlertUI()
  const [comment, setComment] = useState('')
  const [qualityTypes, setQualityTypes] = useState([])

  const [selectedQualityType, setSelectedQualityType] = useState('')
  const [loading, setLoading] = useState(false)
  // S17/S18: a NOK report on a job with auto-consume + Machine Storage asks the worker whether
  // that specific piece consumed material before posting, instead of a plain "quality type" pick.
  const [consumeQuestionOpen, setConsumeQuestionOpen] = useState(false)

  useEffect(() => {
    const getQualityTypes = async () => {
      try {
        const response = await apiGet('qualityTypesAll')
        const filteredQualityTypes = hideOkOption ? response.filter((item) => item.code !== 'Ok') : response
        setQualityTypes(filteredQualityTypes)
        const defaultQualityType = (!hideOkOption && filteredQualityTypes.find((item) => item.code === 'Ok')) || filteredQualityTypes[0]
        if (defaultQualityType) {
          setSelectedQualityType(defaultQualityType.id)
        } else {
          setSelectedQualityType('')
        }
      } catch (error) {
        showAlert({ severity: 'error', title: t_(error.message) })
      }
    }
    if (open) {
      getQualityTypes()
      setConsumeQuestionOpen(false)
    }
    // showAlert/t_ are intentionally excluded: they are only used for the (rare) error path and
    // are recreated on every render by their hooks, which would otherwise re-trigger this fetch on
    // every render instead of only when the dialog opens or hideOkOption changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hideOkOption])

  // S17/S18: the report line only carries `consumeMaterial` when the NOK question was actually
  // asked and answered — `undefined` means "not applicable" (OK line, or no auto-consume/Machine
  // Storage), never sent so the backend request body is unchanged in that case.
  const submitReport = async (consumeMaterial) => {
    setLoading(true)
    try {
      const quantityReport = {
        comment,
        qualityType: selectedQualityType,
        quantity: Number(quantity)
      }
      if (typeof consumeMaterial === 'boolean') {
        quantityReport.consumeMaterial = consumeMaterial
      }
      const result = await apiPost('mos/fabricationOrderReportQuantity/' + worker.id + '/' + device.id + '/' + fabricationOrder.id + '/' + jobReference.id, [quantityReport])
      // S37/S39/S19: bubble the resolved outcome up — the parent container's AlertElement stays
      // mounted after this dialog closes, so it (not this dialog) is what actually shows it.
      const outcome = resolveReportOutcome({
        result,
        requestedQuantity: Number(quantity),
        autoReportResources: getJobAutoReportResources(activeJobs, fabricationOrder, jobReference),
        hasPendingResources: hasPendingRawMaterialResources(materials, node),
        t_
      })
      if (onSuccess) onSuccess(outcome)
      else onCancel()
    } catch (error) {
      showAlert({ severity: 'error', title: t_(error.message) })
    } finally {
      setLoading(false)
    }
  }

  const isNokSelection = () => {
    const selected = qualityTypes.find((item) => item.id === selectedQualityType)
    return Boolean(selected) && selected.code !== OK_QUALITY_TYPE_CODE
  }

  const accept = async () => {
    // S17/S18: NOK + auto-consume + Machine Storage asks the question first — the report is only
    // posted once the worker answers (answerConsumeQuestion below).
    const autoReportResources = getJobAutoReportResources(activeJobs, fabricationOrder, jobReference)
    if (isNokSelection() && autoReportResources && machineStorage?.present) {
      setConsumeQuestionOpen(true)
      return
    }
    await submitReport()
  }

  const answerConsumeQuestion = async (consumeMaterial) => {
    setConsumeQuestionOpen(false)
    await submitReport(consumeMaterial)
  }

  return (
    <>
      <MosSmartDialog
        title={createDialogTitle({ color: 'primary', title: t_('Report quantity') })}
        setOpen={open}
        cancelCallback={onCancel}
        acceptCallback={accept}
        accept={!consumeQuestionOpen}
        disableButtons={loading}
      >
        {AlertElement}
        {consumeQuestionOpen
          ? (
            <Stack spacing={2} sx={{ mt: 1, p: theme => theme.tokens.spacing.md }}>
              <Typography variant='h6' align='center' sx={{ fontSize: theme => theme.tokens.typography.h2 }}>
                {t_('Did this piece consume material?')}
              </Typography>
              <Stack direction='row' spacing={2} justifyContent='center'>
                <Button variant='contained' color='primary' disabled={loading} onClick={() => answerConsumeQuestion(true)}>
                  {t_('YES')}
                </Button>
                <Button variant='outlined' disabled={loading} onClick={() => answerConsumeQuestion(false)}>
                  {t_('NO')}
                </Button>
              </Stack>
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1, visibility: loading ? 'visible' : 'hidden' }}>
                <CircularProgress size={24} />
              </Box>
            </Stack>
            )
          : (
            <Stack spacing={2} sx={{ mt: 1, p: theme => theme.tokens.spacing.md }}>
              <Typography variant='h6' align='center' sx={{ fontSize: theme => theme.tokens.typography.h2 }}>
                {t_('Are you sure you want to report {{quantity}} units?', { quantity })}
              </Typography>

              <FormControl fullWidth size='small' sx={{ mt: 1 }}>
                <InputLabel id='quality-type-select-label'>{t_('Quality type')}</InputLabel>
                <Select
                  labelId='quality-type-select-label'
                  value={selectedQualityType}
                  label={t_('Quality Type')}
                  onChange={(e) => setSelectedQualityType(e.target.value)}
                >
                  {qualityTypes.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.code}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={3}
                value={comment}
                label={t_('Comment')}
                onChange={(e) => setComment(e.target.value)}
                size='small'
                sx={{ mt: 1 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'center', color: 'text.secondary', fontSize: theme => theme.tokens.typography.caption }}>
                <span>{t_('This action will be recorded with your user and device')}</span>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1, visibility: loading ? 'visible' : 'hidden' }}>
                <CircularProgress size={24} />
              </Box>
            </Stack>
            )}
      </MosSmartDialog>
    </>
  )
}
export default ReportQuantityDialog
